import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button, Group, Loader, Stack, Text } from '@mantine/core';
import { IconCircleCheck, IconAlertCircle } from '@tabler/icons-react';
import { api } from '../../../api/index';
import { notifications } from '@mantine/notifications';
import { StatusIcon, VendorAuthLayout } from './VendorAuthLayout';
import { BRAND_MEDIUM } from '../../../theme/brand';

export function VendorVerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');
  const [hasVerified, setHasVerified] = useState(false);

  useEffect(() => {
    // Prevent multiple verification attempts
    if (hasVerified) return;

    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('Verification token is missing. Please check your email for the correct verification link.');
      return;
    }

    // Verify email
    const verifyEmail = async () => {
      try {
        const response = await api.get(`/vendors/verify-email?token=${token}`);
        setHasVerified(true);
        setStatus('success');
        setMessage(response.data.message || 'Email verified successfully!');

        notifications.show({
          title: 'Email Verified',
          message: 'Your email has been verified. You can now log in.',
          color: 'green',
        });

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/vendor/login');
        }, 3000);
      } catch (error: any) {
        const errorMessage = error.response?.data?.error || 'Failed to verify email. The link may be invalid or expired.';

        // If error says "already verified", treat it as success
        if (errorMessage.toLowerCase().includes('already verified')) {
          setHasVerified(true);
          setStatus('success');
          setMessage('Your email has already been verified. You can now log in.');
          notifications.show({
            title: 'Email Already Verified',
            message: 'Your email was already verified. You can log in now.',
            color: 'green',
          });
          setTimeout(() => {
            navigate('/vendor/login');
          }, 3000);
        } else {
          setStatus('error');
          setMessage(errorMessage);
        }
      }
    };

    verifyEmail();
  }, [searchParams, navigate, hasVerified]);

  if (status === 'loading') {
    return (
      <VendorAuthLayout title="Verifying your email" subtitle="This only takes a moment.">
        <Group gap="sm" aria-live="polite">
          <Loader size="sm" color={BRAND_MEDIUM} />
          <Text c="dimmed">Checking your verification link…</Text>
        </Group>
      </VendorAuthLayout>
    );
  }

  if (status === 'success') {
    return (
      <VendorAuthLayout
        icon={
          <StatusIcon>
            <IconCircleCheck size={30} aria-hidden />
          </StatusIcon>
        }
        title="Email verified"
        subtitle={`${message} Taking you to sign in…`}
      >
        <Button component={Link} to="/vendor/login" color={BRAND_MEDIUM} fullWidth size="md" radius="md">
          Sign in now
        </Button>
      </VendorAuthLayout>
    );
  }

  return (
    <VendorAuthLayout
      icon={
        <StatusIcon color="red">
          <IconAlertCircle size={30} aria-hidden />
        </StatusIcon>
      }
      title="We couldn't verify your email"
      subtitle={message}
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Sign in to request a new verification email, or register again if your account was never created.
        </Text>
        <Button component={Link} to="/vendor/login" color={BRAND_MEDIUM} fullWidth size="md" radius="md">
          Go to sign in
        </Button>
        <Button component={Link} to="/vendor/register" variant="default" fullWidth size="md" radius="md">
          Register again
        </Button>
      </Stack>
    </VendorAuthLayout>
  );
}
